import { api } from '../../../utility/apiClient';

// Swap mock flags to `false` once the backend endpoints are live
const USE_MOCK = true;

const MOCK_KPIS = {
  completed: { value: 48, sub: "+12 this month" },
  ongoing:   { value: 7,  sub: "require attention" },
  remaining: { value: 27, sub: "of 50 monthly" },
};

const MOCK_RECENT = [
  { company: "Meta Platforms", date: "Apr 3, 2026" },
  { company: "Shopify Inc.",   date: "Apr 2, 2026" },
  { company: "Notion Labs",    date: "Apr 1, 2026" },
  { company: "Stripe Inc.",    date: "Mar 30, 2026" },
  { company: "Twilio Corp.",   date: "Mar 28, 2026" },
];

const MOCK_ONGOING = [
  { company: "Meta Platforms", date: "Apr 3, 2026",  estimatedTime: "12 Mins" },
  { company: "Shopify Inc.",   date: "Apr 2, 2026",  estimatedTime: "9 Mins" },
  { company: "Notion Labs",    date: "Apr 1, 2026",  estimatedTime: "3 Mins" },
  { company: "Stripe Inc.",    date: "Mar 30, 2026", estimatedTime: "21 Mins" },
  { company: "Twilio Corp.",   date: "Mar 28, 2026", estimatedTime: "5 Mins" },
];

export const getKpis            = () => USE_MOCK ? Promise.resolve(MOCK_KPIS)    : api.get('/dashboard/kpis');
export const getRecentAnalyses  = () => USE_MOCK ? Promise.resolve(MOCK_RECENT)  : api.get('/analyses/recent');
export const getOngoingAnalyses = () => USE_MOCK ? Promise.resolve(MOCK_ONGOING) : api.get('/analyses/ongoing');

// ── Job stats endpoint ───────────────────────────────────────────────────────
/**
 * GET /api/v1/jobs/stats
 * @returns {{ success, message, data: { user_id, total, counts: { completed, running, failed, queued } } }}
 */
export const getJobStats = () => api.get('/api/v1/jobs/stats');

// ── Policy analysis endpoints (always live — no mock) ─────────────────────────
/**
 * POST /api/v1/assets
 * @param {FormData} formData  Fields: software_name*, privacy_url*, notes, policyFile
 * @returns {{ success, message, data: { id, analysis_status } }}
 */
export const createAsset = (formData) => api.postForm('/api/v1/assets', formData);

/**
 * POST /api/v1/jobs/run-once
 * Synchronously runs the pending job and returns the full revision with report_json.
 */
export const runJob = () => api.post('/api/v1/jobs/run-once');

// ── History endpoint ──────────────────────────────────────────────────────────
const ANALYSIS_STATUS = ['pending', 'processing', 'complete', 'failed'];

/**
 * GET /api/v1/history
 * @param {{ page?: number, limit?: number, analysis_status?: string, search?: string }} params
 */
export const getHistory = ({ page = 1, limit = 20, analysis_status, search } = {}) => {
  const qs = new URLSearchParams({ page, limit });
  if (analysis_status && ANALYSIS_STATUS.includes(analysis_status)) {
    qs.set('analysis_status', analysis_status);
  }
  if (search?.trim()) {
    qs.set('search', search.trim());
  }
  return api.get(`/api/v1/history?${qs.toString()}`);
};

/**
 * GET /api/v1/history/last
 * Returns the most recent analysis for the current user.
 */
export const getLastAnalysis = () => api.get('/api/v1/history/last');

/**
 * GET /api/v1/history/asset/{assetId}/report
 * Returns the full analysis report for a specific asset.
 * @param {string|number} assetId
 */
export const getAssetReport = (assetId) =>
  api.get(`/api/v1/history/asset/${assetId}/report`);

/**
 * GET /api/v1/history/revision/{revisionId}/report
 * Returns the full analysis report for a specific revision.
 * @param {string|number} revisionId
 */
export const getRevisionReport = (revisionId) =>
  api.get(`/api/v1/history/revision/${revisionId}/report`);

/**
 * GET /api/v1/assets/{assetId}/revisions/compare?from={revisionId}&to={revisionId}
 * Returns comparison payload between two revisions for a specific asset.
 * @param {string|number} assetId
 * @param {string|number} fromRevisionId
 * @param {string|number} toRevisionId
 */
export const getRevisionComparison = (assetId, fromRevisionId, toRevisionId) => {
  const qs = new URLSearchParams({
    from: String(fromRevisionId),
    to: String(toRevisionId),
  });
  return api.get(`/api/v1/assets/${assetId}/revisions/compare?${qs.toString()}`);
};

/**
 * POST /api/v1/assets/{assetId}/reanalyse
 * Re-runs analysis for an already-analysed asset (against its last stored document).
 * @param {string|number} assetId
 */
export const reanalyseAsset = (assetId) =>
  api.post(`/api/v1/assets/${assetId}/reanalyse`);

/**
 * DELETE /api/v1/assets/{assetId}
 * Soft-deletes the asset and hides all its analyses/versions/documents.
 * @param {string|number} assetId
 */
export const deleteAsset = (assetId) =>
  api.delete(`/api/v1/assets/${assetId}`);

/**
 * POST /api/v1/assets/{assetId}/reanalyse  (multipart)
 * Uploads a NEW policy document version, then re-runs analysis against it.
 * @param {string|number} assetId
 * @param {FormData} formData  Field: policyFile
 */
export const reanalyseAssetWithFile = (assetId, formData) =>
  api.postForm(`/api/v1/assets/${assetId}/reanalyse`, formData);

// ── Policy documents (versioned uploads) ──────────────────────────────────────
/**
 * GET /api/v1/assets/{assetId}/documents
 * Lists uploaded policy-document versions for an asset (no file bytes).
 */
export const getPolicyDocuments = (assetId) =>
  api.get(`/api/v1/assets/${assetId}/documents`);

/**
 * GET /api/v1/assets/{assetId}/documents/compare?from=&to=
 * Returns a text diff between two uploaded document versions.
 */
export const comparePolicyDocuments = (assetId, fromDocId, toDocId) => {
  const qs = new URLSearchParams({ from: String(fromDocId), to: String(toDocId) });
  return api.get(`/api/v1/assets/${assetId}/documents/compare?${qs.toString()}`);
};

/**
 * Downloads a policy-document version (auth-aware) and triggers a browser save.
 * @param {string|number} assetId
 * @param {string|number} documentId
 * @param {string} [fileName]
 */
export const downloadPolicyDocument = async (assetId, documentId, fileName) => {
  const blob = await api.getBlob(`/api/v1/assets/${assetId}/documents/${documentId}/download`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || `policy-document-${documentId}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ── Archived policy page (the source PDF captured during analysis) ───────────
// Distinct from the policy *documents* above, which are files a user uploaded.
// These are the vendor's own page as we fetched and rendered it at analysis time.

/**
 * GET /api/v1/assets/:assetId/snapshot
 * @returns {Promise<{ success, data: { snapshot: object|null, last_fetch_status, privacy_url } }>}
 */
export const getAssetSnapshot = (assetId) =>
  api.get(`/api/v1/assets/${assetId}/snapshot`);

/**
 * GET /api/v1/assets/:assetId/snapshots — full archive history for the URL.
 */
export const getAssetSnapshots = (assetId) =>
  api.get(`/api/v1/assets/${assetId}/snapshots`);

/**
 * Open the archived source PDF in a new tab. Needs an authenticated fetch, so
 * the file is pulled as a blob first rather than linked to directly.
 * @param {number|string} assetId
 * @param {string} [fileName]  used when the browser falls back to downloading
 */
export const openAssetSnapshotPdf = async (assetId, fileName) => {
  const blob = await api.getBlob(`/api/v1/assets/${assetId}/snapshot/download`);
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, '_blank', 'noopener');
  if (!opened) {
    // Popup blocked — fall back to a download so the user still gets the file.
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || `policy-source-${assetId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // Give the new tab time to load before revoking the object URL.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

/**
 * GET /api/v1/assets/vendors/:vendorId/suggested-documents
 * Other policies already analysed on this vendor's domain that the user isn't
 * tracking. Each is a cache hit, so adopting one is instant and costs nothing.
 */
export const getSuggestedDocuments = (vendorId) =>
  api.get(`/api/v1/assets/vendors/${vendorId}/suggested-documents`);

/** GET /api/v1/assets/vendors/:vendorId/comparison-options */
export const getVendorComparisonOptions = (vendorId) =>
  api.get(`/api/v1/assets/vendors/${vendorId}/comparison-options`);

/** GET /api/v1/assets/compare-snapshots?from=&to= */
export const compareSnapshots = (fromId, toId) =>
  api.get(`/api/v1/assets/compare-snapshots?from=${fromId}&to=${toId}`);

// ── Payments ──────────────────────────────────────────────────────────────────
/**
 * POST /api/v1/payments/create-subscription
 * @param {{ plan_id: string }} body
 * @returns {Promise<object>}
 */
export const createSubscription = (body) =>
  api.post('/api/v1/payments/create-subscription', body);

/**
 * GET /api/v1/payments/verify-subscription/:subscriptionId
 * @param {string} subscriptionId
 * @returns {Promise<{ success: boolean, data: { status: 'active' | 'processing' | 'failed', plan_status: string, current_plan_id: string, subscription_id: string } }>}
 */
export const verifySubscription = async (subscriptionId) => {
  console.log('🔍 Calling verifySubscription API for:', subscriptionId);
  
  if (!subscriptionId) {
    throw new Error('Subscription ID is required');
  }
  
  try {
    const response = await api.get(`/api/v1/payments/verify-subscription/${subscriptionId}`);
    console.log('📥 Verify subscription API response:', response);
    return response;
  } catch (error) {
    console.error('❌ Verify subscription API failed:', {
      subscriptionId,
      error: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    throw error;
  }
};

/**
 * ✅ STEP 3: Create Payment Intent
 * POST /api/v1/payments/create-intent
 * @param {{ plan_id: string, currency: string, amount?: number }} body
 * @returns {Promise<{success: boolean, data: {payment_intent: {id: string, client_secret: string, status: string, amount: number, currency: string}, transaction: object, plan: object}}>}
 */
// ── Individual plan trial (POST /api/v1/subscriptions) ──────────────────────
/**
 * POST /api/v1/subscriptions
 * Creates a Stripe subscription with trial for Individual plan users.
 * @param {{ plan_id: string, payment_method_id: string }} body
 */
/**
 * GET /api/v1/policy-maps/{id}/report
 * Returns the full shared report for a policy map entry.
 * @param {string|number} policyMapId
 */
export const getPolicyMapReport = (policyMapId) =>
  api.get(`/api/v1/policy-maps/${policyMapId}/report`);

/**
 * GET /api/v1/jobs/domain-stats
 * Returns shared analysis statistics, my_reports, top_urls, and daily events.
 * @param {{ page?: number, limit?: number }} params
 */
export const getDomainStats = ({ page = 1, limit = 20 } = {}) => {
  const qs = new URLSearchParams({ page, limit });
  return api.get(`/api/v1/jobs/domain-stats?${qs.toString()}`);
};

export const createTrialSubscription = (body) =>
  api.post('/api/v1/subscriptions', body);

/**
 * GET /api/v1/subscriptions/status
 * Returns trial/subscription state for the logged-in user.
 * @returns {{ plan_status, trial_assets_used, trial_assets_limit, ... }}
 */
export const getSubscriptionStatus = () =>
  api.get('/api/v1/subscriptions/status');

/**
 * DELETE /api/v1/subscriptions
 * Cancels the user's active free trial immediately. No charge is made.
 * Only valid when plan_status === 'trialing'.
 */
export const cancelTrialSubscription = () =>
  api.delete('/api/v1/subscriptions');

/**
 * POST /api/v1/subscriptions/upgrade
 * Upgrades or downgrades the user's current active subscription to a new plan.
 */
export const upgradePlan = (body) =>
  api.post('/api/v1/subscriptions/upgrade', body);

export const createPaymentIntent = async (body) => {
  console.log('📤 Sending payment intent request:', body);
  
  try {
    const response = await api.post('/api/v1/payments/create-intent', {
      currency: 'inr',
      ...body,
    });
    
    console.log('📥 Payment intent API response:', response);
    
    // Validate the expected response structure
    if (!response || typeof response !== 'object') {
      throw new Error('Invalid response format from payment API');
    }
    
    if (!response.success) {
      throw new Error(response.message || 'Payment intent creation failed');
    }
    
    if (!response.data?.payment_intent?.client_secret) {
      console.error('Invalid response structure:', response);
      throw new Error('Missing client_secret in payment intent response');
    }
    
    return response;
    
  } catch (error) {
    console.error('❌ Payment intent creation failed:', error);
    
    // Re-throw with more context
    if (error.response?.status === 404) {
      throw new Error('Payment endpoint not found. Please check if /api/v1/payments/create-intent is implemented.');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error during payment initialization. Please try again later.');
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error(error.message || 'Failed to create payment intent');
    }
  }
};
